// Markdown 大綱 → MindMap。寬鬆解析：能理解的盡量理解，問題以診斷訊息回報而非直接失敗。
import { type Diagnostic, type MindMap, type Relation, type Topic, type Structure, STRUCTURES, hash32 } from './model.js';
import { ICON_NAMES } from './icons.js';
import { COLOR_NAMES, THEME_NAMES } from './theme.js';

export interface ParseResult {
  map: MindMap | null;
  diagnostics: Diagnostic[];
}

const MAX_TOPICS = 2000;
const LONG_TITLE = 40;
const ATTR_BLOCK = /\s*\{([^{}]*)\}\s*$/;
const HEADING = /^(#{1,6})\s+(.*)$/;
const BULLET = /^([ \t]*)(?:[-*+]|\d+[.)])\s+(.*)$/;
const NOTE = /^\s*>\s?(?:note:\s*)?(.*)$/i;
const RELATION = /^\s*\[([^\]]+)\]\s*-+>\s*\[([^\]]+)\]\s*(?::\s*(.*))?$/;
const TOPIC_ATTRS = new Set(['id', 'icon', 'color', 'collapsed', 'boundary', 'note']);
const ROOT_ATTRS = new Set([...TOPIC_ATTRS, 'structure', 'theme']);

interface PendingRelation { from: string; to: string; label?: string; line: number }

// 解析 `{key: value, flag}` 屬性區塊；值可用引號包住以容納逗號。
export function parseAttrs(source: string): Map<string, string> {
  const attrs = new Map<string, string>();
  const pattern = /\s*([A-Za-z][\w-]*)\s*(?::\s*("([^"]*)"|'([^']*)'|[^,]*))?\s*(?:,|$)/y;
  let match: RegExpExecArray | null;
  while (pattern.lastIndex < source.length && (match = pattern.exec(source))) {
    if (match[0] === '') break;
    const value = match[3] ?? match[4] ?? match[2]?.trim() ?? 'true';
    attrs.set(match[1]!.toLowerCase(), value);
  }
  return attrs;
}

// 移除標題中的行內 Markdown 標記（粗體、斜體、程式碼、連結）。
export function cleanTitle(text: string): string {
  return text
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/(\*\*|__)(.+?)\1/g, '$2')
    .replace(/(^|[^\w*])[*_](\S(?:.*?\S)?)[*_](?=[^\w*]|$)/g, '$1$2')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

// 主要入口：把大綱文字解析成心智圖與診斷清單。
export function parseOutline(source: string): ParseResult {
  const diagnostics: Diagnostic[] = [];
  const report = (line: number, severity: Diagnostic['severity'], message: string) => diagnostics.push({ line, severity, message });
  const lines = source.replace(/^﻿/, '').split(/\r?\n/);

  let root = null as Topic | null;
  let rootAttrs = new Map<string, string>();
  let rootLine = 0;
  let topicCount = 0;
  const pendingRelations: PendingRelation[] = [];
  const ids = new Map<string, number>();
  const occurrence = new Map<string, number>();
  // stack 的 depth：根為 0；heading 階層與 bullet 縮排共同決定深度。
  const stack: { depth: number; topic: Topic }[] = [];
  let headingDepth = 0;
  let bulletIndents: number[] = [];
  let lastTopic = null as Topic | null;
  let lastTopicLine = 0;
  let inFence = false;
  let inComment = false;

  // 依 title 與作者屬性建立主題，並處理 id 與屬性驗證。
  const makeTopic = (rawTitle: string, lineNumber: number, depth: number, parent: Topic | null): Topic | null => {
    let text = rawTitle;
    let attrs = new Map<string, string>();
    const attrMatch = ATTR_BLOCK.exec(text);
    if (attrMatch) {
      attrs = parseAttrs(attrMatch[1]!);
      text = text.slice(0, attrMatch.index);
    }
    const title = cleanTitle(text);
    if (!title) { report(lineNumber, 'error', '主題標題是空的'); return null; }
    if (++topicCount > MAX_TOPICS) { report(lineNumber, 'error', `主題數超過上限 ${MAX_TOPICS}`); return null; }
    if ([...title].length > LONG_TITLE) report(lineNumber, 'warning', `標題過長（${[...title].length} 字），建議精簡到 ${LONG_TITLE} 字以內，細節放 note`);

    const allowed = depth === 0 ? ROOT_ATTRS : TOPIC_ATTRS;
    const topic: Topic = { id: '', title, children: [] };
    for (const [key, value] of attrs) {
      if (!allowed.has(key)) {
        report(lineNumber, 'warning', key === 'structure' || key === 'theme' ? `屬性 ${key} 只能用在中心主題（# 標題）` : `未知屬性 ${key}，已忽略`);
        continue;
      }
      switch (key) {
        case 'id':
          if (!/^[\w-]{1,64}$/.test(value)) report(lineNumber, 'warning', `id「${value}」只能包含英數、底線與連字號`);
          else { topic.id = value; topic.explicitId = true; }
          break;
        case 'icon':
          if (ICON_NAMES.includes(value)) topic.icon = value;
          else report(lineNumber, 'warning', `未知圖示「${value}」，可用：${ICON_NAMES.join(', ')}`);
          break;
        case 'color':
          if (COLOR_NAMES.includes(value) || /^#[0-9a-f]{6}$/i.test(value)) topic.color = value.toLowerCase();
          else report(lineNumber, 'warning', `未知顏色「${value}」，可用：${COLOR_NAMES.join(', ')} 或 #rrggbb`);
          break;
        case 'collapsed':
          topic.collapsed = value !== 'false';
          break;
        case 'boundary':
          topic.boundary = value === 'true' ? '' : value;
          break;
        case 'note':
          topic.note = value;
          break;
      }
    }
    if (depth === 0) rootAttrs = attrs;
    if (!topic.id) {
      // 未指定 id 時以「祖先路徑 + 標題 + 同名序號」雜湊，內容不變則 id 不變。
      const pathKey = `${parent?.id ?? ''}/${title}`;
      const seen = occurrence.get(pathKey) ?? 0;
      occurrence.set(pathKey, seen + 1);
      topic.id = 't' + hash32(`${pathKey}#${seen}`).toString(36);
    }
    if (ids.has(topic.id)) {
      report(lineNumber, topic.explicitId ? 'error' : 'warning', `id「${topic.id}」重複（第 ${ids.get(topic.id)} 行已使用）`);
      topic.id = `${topic.id}-${lineNumber}`;
      topic.explicitId = false;
    }
    ids.set(topic.id, lineNumber);
    lastTopic = topic;
    lastTopicLine = lineNumber;
    return topic;
  };

  // 找出指定深度的父主題（stack 中最後一個深度較淺的項目）。
  const parentFor = (depth: number): Topic | null => {
    for (let index = stack.length - 1; index >= 0; index--) if (stack[index]!.depth < depth) return stack[index]!.topic;
    return null;
  };

  // 把主題接到 stack 中正確的父節點底下。
  const attach = (topic: Topic, depth: number) => {
    while (stack.length && stack[stack.length - 1]!.depth >= depth) stack.pop();
    stack[stack.length - 1]?.topic.children.push(topic);
    stack.push({ depth, topic });
  };

  // 設定中心主題並重置階層狀態。
  const setRoot = (topic: Topic, lineNumber: number, nextHeadingDepth: number) => {
    root = topic;
    rootLine = lineNumber;
    stack.length = 0;
    stack.push({ depth: 0, topic });
    headingDepth = nextHeadingDepth;
    bulletIndents = [];
  };

  lines.forEach((rawLine, index) => {
    const lineNumber = index + 1;
    const line = rawLine.replace(/\s+$/, '');
    if (/^\s*(```|~~~)/.test(line)) { inFence = !inFence; return; }
    if (inFence) return;
    if (inComment) { if (line.includes('-->')) inComment = false; return; }
    const content = line.replace(/<!--.*?-->/g, '');
    if (content.includes('<!--')) { inComment = true; return; }
    if (!content.trim() || /^\s*(---+|\*\*\*+)\s*$/.test(content)) return;

    const relation = RELATION.exec(content);
    if (relation) {
      pendingRelations.push({ from: relation[1]!.trim(), to: relation[2]!.trim(), label: relation[3]?.trim() || undefined, line: lineNumber });
      return;
    }

    const heading = HEADING.exec(content);
    if (heading) {
      const level = heading[1]!.length;
      if (!root) {
        if (level !== 1) report(lineNumber, 'warning', `中心主題建議用一個 #（目前是 ${'#'.repeat(level)}）`);
        const topic = makeTopic(heading[2]!, lineNumber, 0, null);
        if (topic) setRoot(topic, lineNumber, 0);
        return;
      }
      if (level === 1) report(lineNumber, 'warning', '出現第二個 # 標題，已當作第 1 層分支；一張圖只應有一個中心主題');
      // ## 為第 1 層、### 為第 2 層…；不允許跳層。
      const depth = Math.min(Math.max(1, level - 1), headingDepth + 1);
      const topic = makeTopic(heading[2]!, lineNumber, depth, parentFor(depth));
      if (!topic) return;
      attach(topic, depth);
      headingDepth = depth;
      bulletIndents = [];
      return;
    }

    const bullet = BULLET.exec(content);
    if (bullet) {
      const indent = bullet[1]!.replace(/	/g, '    ').length;
      while (bulletIndents.length && bulletIndents[bulletIndents.length - 1]! > indent) bulletIndents.pop();
      if (!bulletIndents.length || bulletIndents[bulletIndents.length - 1]! < indent) bulletIndents.push(indent);
      const title = bullet[2]!.replace(/^\[[ xX]\]\s+/, '');
      if (!root) {
        // 沒有 # 標題時，第一個頂層項目當中心主題，其子項目為第 1 層。
        const topic = makeTopic(title, lineNumber, 0, null);
        if (topic) { setRoot(topic, lineNumber, -1); bulletIndents = [indent]; }
        return;
      }
      let depth = headingDepth + bulletIndents.length;
      if (depth < 1) {
        report(lineNumber, 'warning', '出現第二個頂層項目，已當作第 1 層分支；請用「# 標題」指定中心主題');
        depth = 1;
      }
      const topic = makeTopic(title, lineNumber, depth, parentFor(depth));
      if (topic) attach(topic, depth);
      return;
    }

    const note = NOTE.exec(content);
    if (note && content.trimStart().startsWith('>')) {
      const target = lastTopic as Topic | null;
      if (!target) { report(lineNumber, 'warning', '備註前面沒有主題，已忽略'); return; }
      target.note = target.note ? `${target.note}
${note[1]}` : note[1]!;
      return;
    }

    // 緊接在主題後的純文字視為備註；其餘忽略並警告。
    if (lastTopic && lineNumber === lastTopicLine + 1) {
      const target = lastTopic as Topic;
      target.note = target.note ? `${target.note}
${content.trim()}` : content.trim();
      lastTopicLine = lineNumber;
      return;
    }
    report(lineNumber, 'warning', '無法辨識的行，已忽略（主題請用 # 或 - 開頭）');
  });

  if (inFence) report(lines.length, 'warning', '程式碼區塊未關閉');

  if (!root) {
    report(1, 'error', '找不到中心主題：請以「# 標題」開頭，分支用「- 」列出');
    return { map: null, diagnostics };
  }

  const rootTopic: Topic = root;
  let structure: Structure = 'balanced';
  const structureValue = rootAttrs.get('structure');
  if (structureValue) {
    if ((STRUCTURES as readonly string[]).includes(structureValue)) structure = structureValue as Structure;
    else report(rootLine, 'warning', `未知結構「${structureValue}」，可用：${STRUCTURES.join(', ')}`);
  }
  let theme = 'sketch';
  const themeValue = rootAttrs.get('theme');
  if (themeValue) {
    if (THEME_NAMES.includes(themeValue)) theme = themeValue;
    else report(rootLine, 'warning', `未知主題「${themeValue}」，可用：${THEME_NAMES.join(', ')}`);
  }

  const relations = resolveRelations(rootTopic, pendingRelations, report);
  if (!rootTopic.children.length) report(rootLine, 'warning', '中心主題沒有任何分支');
  if (rootTopic.children.length > 10) report(rootLine, 'warning', `第 1 層有 ${rootTopic.children.length} 個分支，建議 3～8 個，太多時請歸類`);
  return { map: { root: rootTopic, relations, structure, theme }, diagnostics };
}

// 以 #id 或完整標題解析關聯線兩端。
function resolveRelations(root: Topic, pending: PendingRelation[], report: (line: number, severity: Diagnostic['severity'], message: string) => void): Relation[] {
  const byId = new Map<string, Topic>();
  const byTitle = new Map<string, Topic[]>();
  const visit = (topic: Topic) => {
    byId.set(topic.id, topic);
    const list = byTitle.get(topic.title) ?? [];
    list.push(topic);
    byTitle.set(topic.title, list);
    topic.children.forEach(visit);
  };
  visit(root);
  const find = (reference: string, line: number): Topic | null => {
    if (reference.startsWith('#')) {
      const topic = byId.get(reference.slice(1));
      if (!topic) report(line, 'error', `關聯線找不到 id「${reference.slice(1)}」`);
      return topic ?? null;
    }
    const matches = byTitle.get(cleanTitle(reference)) ?? [];
    if (!matches.length) { report(line, 'error', `關聯線找不到標題「${reference}」`); return null; }
    if (matches.length > 1) report(line, 'warning', `標題「${reference}」有 ${matches.length} 個，已連到第一個；請改用 {id: ...} 與 [#id]`);
    return matches[0]!;
  };
  const relations: Relation[] = [];
  for (const item of pending) {
    const from = find(item.from, item.line);
    const to = find(item.to, item.line);
    if (!from || !to) continue;
    if (from === to) { report(item.line, 'warning', '關聯線兩端是同一個主題，已忽略'); continue; }
    relations.push({ from: from.id, to: to.id, ...(item.label ? { label: item.label } : {}) });
  }
  return relations;
}
